import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { formatApiErrorBody } from '@/lib/formatApiError';
import { notify } from '@/lib/notify';
import { ContentPageHeader, TableLink, TablePagination } from '@/components/global';
import { entityLinkClass } from '@/lib/entityLink';
import { InventoryDataTableCard, InventoryTableHead } from '@/components/inventory';
import { MarginBadge } from '@/components/products';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MoreHorizontal, Plus, LayoutGrid } from 'lucide-react';
import {
  PlanogramListItem,
  downloadPlanogramExcel,
  type PlanogramExportAudience,
} from './planogram';

const PAGE_SIZE = 20;

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function PlanogramsListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<PlanogramListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlanogramListItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/planograms');
      if (!res.ok) throw new Error('Failed to load planograms');
      const data: { items: PlanogramListItem[] } = await res.json();
      setItems(data.items);
    } catch {
      setError('Unable to load planograms. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [items, page]
  );

  const handleDuplicate = useCallback(
    async (item: PlanogramListItem) => {
      setBusyId(item.id);
      try {
        const res = await apiFetch(`/api/v1/planograms/${item.id}/duplicate`, {
          method: 'POST',
        });
        if (!res.ok) throw new Error(formatApiErrorBody(await res.json().catch(() => ({}))));
        notify.success('Planogram duplicated');
        await load();
      } catch (err) {
        notify.error(err instanceof Error ? err.message : 'Failed to duplicate planogram');
      } finally {
        setBusyId(null);
      }
    },
    [load]
  );

  const handleExport = useCallback(async (
    item: PlanogramListItem,
    audience: PlanogramExportAudience
  ) => {
    setBusyId(item.id);
    try {
      await downloadPlanogramExcel(item.id, audience);
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to export planogram');
    } finally {
      setBusyId(null);
    }
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      const res = await apiFetch(`/api/v1/planograms/${deleteTarget.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete planogram');
      notify.success('Planogram deleted');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to delete planogram');
    } finally {
      setBusyId(null);
    }
  }, [deleteTarget, load]);

  const header = (
    <>
      <InventoryTableHead className="pl-5">Name</InventoryTableHead>
      <InventoryTableHead className="hidden sm:table-cell">Slots filled</InventoryTableHead>
      <InventoryTableHead>Blended margin</InventoryTableHead>
      <InventoryTableHead className="hidden md:table-cell">Target</InventoryTableHead>
      <InventoryTableHead className="hidden lg:table-cell">Updated</InventoryTableHead>
      <InventoryTableHead className="pr-5 text-right">Actions</InventoryTableHead>
    </>
  );

  return (
    <div className="space-y-6">
      <ContentPageHeader
        title="Planograms"
        description="Draft slot layouts for planning and margin targeting. Export as Excel to share with images."
        actions={
          <Button onClick={() => navigate('/planograms/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New planogram
          </Button>
        }
      />

      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </div>
      ) : null}

      <InventoryDataTableCard
        loading={loading}
        isEmpty={items.length === 0}
        colSpan={6}
        emptyMessage="No planograms yet. Create your first draft to start planning."
        header={header}
        footer={
          items.length > PAGE_SIZE ? (
            <TablePagination
              page={page}
              totalPages={totalPages}
              totalItems={items.length}
              itemLabel="planograms"
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          ) : undefined
        }
      >
        {pageItems.map((item) => (
          <TableRow
            key={item.id}
            className="group cursor-pointer"
            onClick={() => navigate(`/planograms/${item.id}`)}
          >
            <TableCell className="pl-5 font-medium">
              <TableLink
                to={`/planograms/${item.id}`}
                className={entityLinkClass('inline-flex items-center gap-1.5')}
                onClick={(e) => e.stopPropagation()}
              >
                <LayoutGrid className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                {item.name}
              </TableLink>
            </TableCell>
            <TableCell className="hidden tabular-nums text-muted-foreground sm:table-cell">
              {item.slotsFilled}/{item.slotCount}
            </TableCell>
            <TableCell>
              {item.blendedMarginPercent != null ? (
                <MarginBadge
                  marginPercent={item.blendedMarginPercent}
                  targetMargin={item.targetMarginPercent}
                  showTarget
                />
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell className="hidden tabular-nums text-muted-foreground md:table-cell">
              {item.targetMarginPercent.toFixed(0)}%
            </TableCell>
            <TableCell className="hidden text-muted-foreground lg:table-cell">
              {formatDate(item.updatedAt)}
            </TableCell>
            <TableCell className="pr-5 text-right">
              <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      disabled={busyId === item.id}
                      aria-label={`Actions for ${item.name}`}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/planograms/${item.id}`)}>
                      Open
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDuplicate(item)}>
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport(item, 'internal')}>
                      Export internal Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport(item, 'external')}>
                      Export external Excel
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setDeleteTarget(item)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </InventoryDataTableCard>

      <Dialog open={deleteTarget != null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete planogram?</DialogTitle>
            <DialogDescription>
              This permanently removes the draft “{deleteTarget?.name}”. Live machine planograms are
              not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busyId === deleteTarget?.id}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
